// Gas giant band shader — latitude-gated UV shift that mimics
// differential rotation. Jupiter's equator completes one rotation
// ~5 minutes faster than its poles, and Saturn's equatorial zone
// runs about 25 minutes faster than its magnetic rotation rate.
// Real gas giants have no rigid surface, so different latitudes
// literally spin at different rates and the belts / storms visibly
// shear over time.
//
// Implementation: we hook into MeshLambertMaterial via onBeforeCompile,
// pass the mesh-local position through as a varying, and replace the
// stock map_fragment chunk with a sample that offsets vMapUv.x by
// time * rate * cos²(lat). cos²(lat) peaks at the equator and falls
// to zero at the poles, so polar regions stay locked to the mesh
// rotation while equatorial bands drift east.
//
// Using onBeforeCompile rather than a full ShaderMaterial keeps every
// scene light (sun PointLight, ambient, hemisphere) working unchanged.

import * as THREE from 'three';
import { OKLAB_MIX_GLSL } from './oklab.js';

// Inject OKLAB_MIX_GLSL into a Three.js compiled fragment shader if
// it isn't already present. Multiple onBeforeCompile hooks may run
// for the same material (bands, limb, lightning); we want oklchMix
// available in all of them but defined only once to avoid duplicate
// function compile errors. The presence check on "vec3 oklchMix"
// keeps subsequent calls idempotent.
function injectOklabHelper(shader) {
  if (shader.fragmentShader.includes('vec3 oklchMix')) return;
  shader.fragmentShader = shader.fragmentShader
    .replace('#include <common>', `#include <common>\n${OKLAB_MIX_GLSL}`);
}

const vertexHeaderAddition = `
varying vec3 vGasLocalPos;
varying vec3 vGasWorldPos;
`;

const vertexBodyAddition = `
vGasLocalPos = position;
vGasWorldPos = (modelMatrix * vec4(position, 1.0)).xyz;
`;

const fragmentHeaderAddition = `
varying vec3 vGasLocalPos;
varying vec3 vGasWorldPos;
uniform float bandTime;
uniform float bandRate;
uniform float seamBlendWidth;
uniform float seamBlendStrength;
// Per-spot animated highlight (Jupiter Great Red Spot etc.). Center
// in pre-shift UV so the spot tracks the texture's drift, not the
// mesh. Strength = 0 → no spot effect (Saturn etc. pass 0).
uniform vec2 redSpotCenterUV;
uniform vec2 redSpotRadiusUV;
uniform float redSpotStrength;
uniform vec3 redSpotTint;
// Close-up procedural detail synthesis. The 4K base texture pixelates
// when the camera fills the screen with Jupiter; rather than ship a
// 16K texture, we synthesise band-aligned high-frequency fbm noise
// per fragment that fades in as the camera approaches. Detail is
// effectively infinite-resolution (procedural), so zoom is no longer
// bounded by texel size.
uniform float gasDetailStrength;
uniform float gasDetailNearDistance;
uniform float gasDetailFarDistance;
uniform float gasBodyRadius;
// Per-planet sunlight multiplier (shared uniform contract; main.js
// wires it). <= 0.001 means "unwired" → fall back to 1.0 so the
// render is unchanged until the CPU side sets it.
uniform float uSunIntensity;
// Image-space sharpen (unsharp mask) + vibrance. Neutral defaults
// (sharpen 0, saturation 1, contrast 0) leave the disc untouched, so
// only the bodies that opt in (Jupiter) get the effect. gasSharpenTexel
// is one texel of the base map in UV so the 4-tap high-pass scales with
// the texture's real resolution.
uniform float gasSharpen;
uniform vec2  gasSharpenTexel;
uniform float gasSaturation;
uniform float gasContrast;
// Extra per-body film grain, layered on top of the global post-process
// grain so an opted-in body (Jupiter) reads a touch grainier. gasGrainTime
// is wall-clock driven (NOT bandTime, which freezes when sim time is
// paused) so the grain shimmers per frame instead of locking to the disc.
uniform float gasGrain;
uniform float gasGrainTime;

// Hash-based value noise. Cheap, no texture lookup, runs per-pixel.
float gasHash(vec2 p) {
  return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453123);
}
// The detail layer is sampled in texture UV, so it has to wrap in U or the
// u=0/1 meridian draws a line where the streaks on either side stop lining
// up. Lattice columns are taken mod periodX (cells across one turn), and
// each octave doubles U exactly so the period stays whole; V keeps 2.07 so
// the octaves don't share a lattice.
float gasValueNoiseWrapX(vec2 p, float periodX) {
  vec2 i = floor(p);
  vec2 f = fract(p);
  vec2 u = f * f * (3.0 - 2.0 * f);
  float x0 = mod(i.x, periodX);
  float x1 = mod(i.x + 1.0, periodX);
  return mix(
    mix(gasHash(vec2(x0, i.y)), gasHash(vec2(x1, i.y)), u.x),
    mix(gasHash(vec2(x0, i.y + 1.0)), gasHash(vec2(x1, i.y + 1.0)), u.x),
    u.y
  );
}
float gasFbmWrapX(vec2 p, float periodX) {
  float v = 0.0;
  float a = 0.5;
  for (int i = 0; i < 5; i++) {
    v += a * gasValueNoiseWrapX(p, periodX);
    p *= vec2(2.0, 2.07);
    periodX *= 2.0;
    a *= 0.5;
  }
  return v;
}
`;

// Replacement for the default <map_fragment> chunk. Same final effect
// (diffuseColor *= sampledDiffuseColor) but with a latitude-dependent
// UV x-shift applied first. Fract() wraps the shift so it tiles
// seamlessly across the 0..1 UV boundary — which is why we also set
// wrapS = RepeatWrapping on the texture at bind time.
//
// textureGrad is important: even with CPU-side phase caps, the shift
// differs between adjacent pixels because cosLatSq varies
// pixel-to-pixel. Clamp the gradients so a screen-space quad that
// straddles SphereGeometry's duplicated u=0/1 seam cannot pick a much
// lower mip level and draw a dotted meridian.
const fragmentMapChunk = `
#ifdef USE_MAP
  vec3 gasNormalLocal = normalize(vGasLocalPos);
  float gasCosLatSq = 1.0 - gasNormalLocal.y * gasNormalLocal.y;
  float gasShift = bandTime * bandRate * gasCosLatSq;
  // Sample at -shift so visible features drift toward +u: the
  // equatorial jet LEADS the mesh rotation (Jupiter's System I runs
  // faster than the interior), matching the procedural ice giants.
  vec2 gasShiftedUv = vec2(fract(vMapUv.x - gasShift), vMapUv.y);
  vec2 gasUvDx = clamp(dFdx(vMapUv), vec2(-0.025), vec2(0.025));
  vec2 gasUvDy = clamp(dFdy(vMapUv), vec2(-0.025), vec2(0.025));
  #ifdef GL_EXT_shader_texture_lod
    vec4 sampledDiffuseColor = texture2DGradEXT(map, gasShiftedUv, gasUvDx, gasUvDy);
  #else
    vec4 sampledDiffuseColor = texture2D(map, gasShiftedUv);
  #endif
  float seamBlend = 0.0;
  if (seamBlendWidth > 0.0 && seamBlendStrength > 0.0) {
    float seamDist = min(gasShiftedUv.x, 1.0 - gasShiftedUv.x);
    seamBlend = (1.0 - smoothstep(0.0, seamBlendWidth, seamDist)) * seamBlendStrength;
    float gasMirrorX = clamp(1.0 - gasShiftedUv.x, 0.0005, 0.9995);
    vec2 gasMirrorUv = vec2(gasMirrorX, gasShiftedUv.y);
    #ifdef GL_EXT_shader_texture_lod
      vec4 seamDiffuseColor = texture2DGradEXT(map, gasMirrorUv, gasUvDx, gasUvDy);
    #else
      vec4 seamDiffuseColor = texture2D(map, gasMirrorUv);
    #endif
    sampledDiffuseColor = mix(sampledDiffuseColor, 0.5 * (sampledDiffuseColor + seamDiffuseColor), seamBlend);
  }

  // Shared camera distance — used by the sharpen fade and the detail
  // synthesis below, computed once.
  float gasViewDist = length(cameraPosition - vGasWorldPos);

  // === SHARPEN (unsharp mask) ===
  // The 4K equirectangular base softens both up close (texels stretch
  // past a screen pixel) and at distance (the disc undersamples the
  // map). A cross-tap high-pass (centre minus the average of four
  // neighbours, added back) restores crisp belt edges and GRS rim so
  // the disc reads as a higher-resolution surface. The tap size adapts
  // to the screen-space sampling rate via fwidth — ~1 texel up close,
  // widening toward the pixel footprint at range — so it sharpens at
  // whatever scale Jupiter is actually drawn, near AND far, instead of
  // only at full-res distance. Clamped so a tap never reaches across a
  // whole band.
  if (gasSharpen > 0.0001) {
    // The base map carries NO mipmaps (any mip filtering reintroduces the
    // meridian seam on an equirectangular sphere), so the taps read level
    // 0 with plain texture2D — there is no LOD to spike at the seam, so
    // no dashed line. Tap size adapts to the screen-space sampling rate
    // so the high-pass works at whatever scale Jupiter is drawn. Faded
    // out at long range: without mipmaps a distant disc undersamples, and
    // sharpening that would shimmer, so the effect tapers off far away.
    vec2 gasStep = clamp(max(gasSharpenTexel, fwidth(vMapUv) * 0.7), vec2(0.0), vec2(0.012));
    vec3 gasNbr =
        texture2D(map, vec2(fract(gasShiftedUv.x + gasStep.x), gasShiftedUv.y)).rgb
      + texture2D(map, vec2(fract(gasShiftedUv.x - gasStep.x), gasShiftedUv.y)).rgb
      + texture2D(map, vec2(gasShiftedUv.x, clamp(gasShiftedUv.y + gasStep.y, 0.0, 1.0))).rgb
      + texture2D(map, vec2(gasShiftedUv.x, clamp(gasShiftedUv.y - gasStep.y, 0.0, 1.0))).rgb;
    vec3 gasBlur = gasNbr * 0.25;
    float gasSharpFade = 1.0 - smoothstep(gasDetailFarDistance * 0.8, gasDetailFarDistance * 2.5, gasViewDist);
    sampledDiffuseColor.rgb += (sampledDiffuseColor.rgb - gasBlur) * gasSharpen * gasSharpFade;
    sampledDiffuseColor.rgb = max(sampledDiffuseColor.rgb, vec3(0.0));
  }

  // === VIBRANCE ===
  // Lift saturation and apply a gentle contrast curve so the belts and
  // zones read with the punch real Hubble/Juno imagery shows instead of
  // the flat warm wash the raw texture gives. Sample is linear (sRGB
  // textures decode on fetch), so the contrast pivot sits low.
  if (gasSaturation != 1.0 || gasContrast != 0.0) {
    float gasLuma = dot(sampledDiffuseColor.rgb, vec3(0.299, 0.587, 0.114));
    sampledDiffuseColor.rgb = mix(vec3(gasLuma), sampledDiffuseColor.rgb, gasSaturation);
    sampledDiffuseColor.rgb = (sampledDiffuseColor.rgb - 0.30) * (1.0 + gasContrast) + 0.30;
    sampledDiffuseColor.rgb = max(sampledDiffuseColor.rgb, vec3(0.0));
  }

  // === PROCEDURAL DETAIL SYNTHESIS ===
  // High-frequency band-aligned fbm that fades in as the camera
  // approaches. The base texture provides the macro band pattern and
  // colour; this layer synthesises the eddies, festoons, and
  // turbulence between band edges that the 4K texture's resolution
  // can't represent. Stretched horizontally (lat band-aligned) and
  // modulated by the underlying texture luminance so the detail
  // emerges out of the existing pattern rather than overlaying it.
  if (gasDetailStrength > 0.0) {
    // Fade in: 0 when far, 1 when near. Range maps to scene units
    // proportional to body radius so it works for Jupiter (radius ~8)
    // and Saturn (radius ~7) without tuning per-body.
    float gasDetailFade = 1.0 - smoothstep(
      gasDetailNearDistance,
      gasDetailFarDistance,
      gasViewDist
    );
    if (gasDetailFade > 0.001) {
      // Latitude-stretched UV for band alignment — bands run E-W so
      // the noise should be ~10× wider in U than V. Sample at 30×
      // the texture's UV space so the detail is finer than a texel.
      // Whole cell counts in U (30 and 10) so both layers wrap at the
      // meridian (gasFbmWrapX).
      vec2 gasDetailUv = vec2(gasShiftedUv.x * 30.0, gasShiftedUv.y * 250.0);
      float gasFineDetail = gasFbmWrapX(gasDetailUv, 30.0);
      float gasMidDetail  = gasFbmWrapX(vec2(gasShiftedUv.x * 10.0, gasShiftedUv.y * 87.5) + vec2(7.3, 4.1), 10.0);
      // Combine two octaves so the detail has both fine-grain and
      // small-eddy scales — fine grain at ~50× texture scale, larger
      // eddies at ~18×.
      float gasDetail = gasFineDetail * 0.65 + gasMidDetail * 0.35;
      // Range-shift so the detail is bipolar (-0.5 .. +0.5) — applied
      // as a multiplicative perturbation, it can both lighten and
      // darken existing pixels, keeping the macro brightness intact.
      gasDetail = (gasDetail - 0.5) * 2.0;
      // Strength: 1 + small bipolar gain, ≈ ±gain perturbation.
      float gain = gasDetailStrength * gasDetailFade * 0.22;
      sampledDiffuseColor.rgb *= 1.0 + gasDetail * gain;
      // Plus a hue-shift component: dark band edges go slightly
      // warmer (orange/brown) and bright zone edges slightly cooler
      // (whitish). Underlying luminance drives the sign so the shift
      // matches the band character.
      float gasLuma = dot(sampledDiffuseColor.rgb, vec3(0.299, 0.587, 0.114));
      vec3 gasWarmShift = vec3(0.08, -0.02, -0.05);
      vec3 gasCoolShift = vec3(-0.02, 0.0, 0.04);
      vec3 gasHueShift = mix(gasWarmShift, gasCoolShift, smoothstep(0.35, 0.75, gasLuma));
      sampledDiffuseColor.rgb += gasHueShift * gasDetail * gain * 0.5;
    }
  }

  diffuseColor *= sampledDiffuseColor;

  // Animated Great Red Spot highlight. Distance to the spot centre
  // in elliptical UV-space gives an isotropic mask; pulse over time
  // simulates the storm "breathing" (real GRS oscillates in size +
  // contrast over weeks). Slight rotation around the spot centre is
  // baked into the time-varying angular offset on the inner detail.
  // Strength uniform = 0 turns the whole effect off (other gas
  // giants pass 0 so the same shader works everywhere).
  if (redSpotStrength > 0.0001) {
    vec2 spotDelta = (gasShiftedUv - redSpotCenterUV) / redSpotRadiusUV;
    float spotDist2 = dot(spotDelta, spotDelta);
    if (spotDist2 < 1.5) {
      // Soft elliptical falloff
      float spotMask = smoothstep(1.2, 0.0, sqrt(spotDist2));
      // Storm breathing — slow ±15% contrast pulse on a ~30s cycle
      float pulse = 0.85 + 0.15 * sin(bandTime * 0.18);
      // Slight inner rotation: rotate spotDelta to bias the angular
      // detail one way over time, faking the storm's slow swirl.
      float spinAngle = bandTime * 0.05;
      float c = cos(spinAngle);
      float s = sin(spinAngle);
      vec2 spotRot = vec2(c * spotDelta.x - s * spotDelta.y,
                          s * spotDelta.x + c * spotDelta.y);
      // Inner brightness gradient: bright core, darker edge ring.
      float innerBright = smoothstep(0.7, 0.0, length(spotRot));
      vec3 spotColor = redSpotTint * (0.4 + innerBright * 0.7) * pulse;
      // OKLCh between the underlying band color and the tinted spot
      // so the storm reads as a coherent red/orange feature rather
      // than passing through a desaturated mid-tone at the soft
      // ellipse edge where spotMask is partial.
      diffuseColor.rgb = oklchMix(diffuseColor.rgb, diffuseColor.rgb * spotColor, spotMask * redSpotStrength);
    }
  }

  // Extra film grain on the disc (opt-in; Jupiter). Screen-space per-pixel
  // hash modulating the albedo, evolving with gasGrainTime so it shimmers
  // like a higher-ISO frame rather than freezing onto the surface. Same
  // hash form as the global cinematic grain so the character matches; this
  // just stacks a little more on this one body.
  if (gasGrain > 0.0001) {
    float gasGrainHash = fract(sin(dot(gl_FragCoord.xy + gasGrainTime, vec2(12.9898, 78.233))) * 43758.5453);
    diffuseColor.rgb *= 1.0 + (gasGrainHash - 0.5) * gasGrain;
  }
#endif
`;

// Scale ONLY the direct (sun PointLight) diffuse by the per-planet
// sunlight multiplier. Ambient / hemisphere fill lands in
// indirectDiffuse and emissive (lightning) in totalEmissiveRadiance,
// so night-side floors and glows are untouched.
const sunIntensityFragmentChunk = `
{
  float sunI = uSunIntensity > 0.001 ? uSunIntensity : 1.0;
  reflectedLight.directDiffuse *= sunI;
}
`;

// Attach differential-rotation behaviour to an existing
// MeshLambertMaterial. `rate` is the UV x-shift added at the equator
// per unit of bandTime — smaller = slower shear. `seamBlendWidth`
// feathers non-tileable equirectangular texture edges after the
// latitude shift so the moving wrap boundary does not draw a hard
// meridian. Returns the uniforms object so the caller can animate
// bandTime each frame.
export function applyGasGiantBands(material, texture, rate, options = {}) {
  const {
    seamBlendWidth = 0,
    seamBlendStrength = 1,
    redSpotCenterUV = new THREE.Vector2(0.83, 0.40),
    redSpotRadiusUV = new THREE.Vector2(0.05, 0.04),
    redSpotStrength = 0,
    redSpotTint = new THREE.Color(0.95, 0.55, 0.45),
    // Procedural detail synthesis. Disabled by default (0); the
    // caller passes a strength in [0,1] for gas giants that benefit
    // from close-up zoom — currently Jupiter/Saturn/Neptune/Uranus
    // all opt in. nearDistance and farDistance are in scene units
    // and gate the fade-in. bodyRadius is passed so the defaults
    // scale per-body without per-body tuning.
    detailStrength = 0,
    bodyRadius = 8.0,
    detailNearDistance = null,
    detailFarDistance = null,
    // Image-space sharpen + vibrance. Neutral defaults (no-op) so any
    // gas giant that doesn't opt in renders exactly as before.
    sharpen = 0,
    saturation = 1.0,
    contrast = 0.0,
    // Extra film grain (0 = none). Opt-in per body.
    grain = 0
  } = options;
  const near = detailNearDistance !== null ? detailNearDistance : bodyRadius * 1.2;
  const far  = detailFarDistance  !== null ? detailFarDistance  : bodyRadius * 9.0;

  // One texel of the base map in UV space, for the unsharp-mask taps.
  // Falls back to a 4K equirectangular assumption if the image hasn't
  // reported its dimensions yet.
  const texW = (texture && texture.image && texture.image.width)  || 4096;
  const texH = (texture && texture.image && texture.image.height) || 2048;
  const sharpenTexel = new THREE.Vector2(1 / texW, 1 / texH);

  // Marked for upload only when the wrap changes: the materials are made
  // again on every focus change, and marking it each time sent Jupiter's
  // 4096 x 2048 map to the GPU again on every click (50 ms in one frame).
  if (texture.wrapS !== THREE.RepeatWrapping) {
    texture.wrapS = THREE.RepeatWrapping;
    texture.needsUpdate = true;
  }
  material.extensions = material.extensions || {};
  material.extensions.derivatives = true;
  material.extensions.shaderTextureLOD = true;
  const previousOnBeforeCompile = material.onBeforeCompile;

  const bandUniforms = {
    bandTime: { value: 0 },
    bandRate: { value: rate },
    seamBlendWidth: { value: seamBlendWidth },
    seamBlendStrength: { value: seamBlendStrength },
    redSpotCenterUV: { value: redSpotCenterUV },
    redSpotRadiusUV: { value: redSpotRadiusUV },
    redSpotStrength: { value: redSpotStrength },
    redSpotTint: { value: redSpotTint },
    gasDetailStrength: { value: detailStrength },
    gasDetailNearDistance: { value: near },
    gasDetailFarDistance: { value: far },
    gasBodyRadius: { value: bodyRadius },
    uSunIntensity: { value: 1 },
    gasSharpen: { value: sharpen },
    gasSharpenTexel: { value: sharpenTexel },
    gasSaturation: { value: saturation },
    gasContrast: { value: contrast },
    gasGrain: { value: grain },
    gasGrainTime: { value: 0 }
  };

  material.onBeforeCompile = (shader) => {
    if (previousOnBeforeCompile) previousOnBeforeCompile(shader);
    injectOklabHelper(shader);
    shader.uniforms.bandTime = bandUniforms.bandTime;
    shader.uniforms.bandRate = bandUniforms.bandRate;
    shader.uniforms.seamBlendWidth = bandUniforms.seamBlendWidth;
    shader.uniforms.seamBlendStrength = bandUniforms.seamBlendStrength;
    shader.uniforms.redSpotCenterUV = bandUniforms.redSpotCenterUV;
    shader.uniforms.redSpotRadiusUV = bandUniforms.redSpotRadiusUV;
    shader.uniforms.redSpotStrength = bandUniforms.redSpotStrength;
    shader.uniforms.redSpotTint = bandUniforms.redSpotTint;
    shader.uniforms.gasDetailStrength = bandUniforms.gasDetailStrength;
    shader.uniforms.gasDetailNearDistance = bandUniforms.gasDetailNearDistance;
    shader.uniforms.gasDetailFarDistance = bandUniforms.gasDetailFarDistance;
    shader.uniforms.gasBodyRadius = bandUniforms.gasBodyRadius;
    shader.uniforms.uSunIntensity = bandUniforms.uSunIntensity;
    shader.uniforms.gasSharpen = bandUniforms.gasSharpen;
    shader.uniforms.gasSharpenTexel = bandUniforms.gasSharpenTexel;
    shader.uniforms.gasSaturation = bandUniforms.gasSaturation;
    shader.uniforms.gasContrast = bandUniforms.gasContrast;
    shader.uniforms.gasGrain = bandUniforms.gasGrain;
    shader.uniforms.gasGrainTime = bandUniforms.gasGrainTime;

    shader.vertexShader = shader.vertexShader
      .replace('#include <common>', `#include <common>\n${vertexHeaderAddition}`)
      .replace('#include <begin_vertex>', `#include <begin_vertex>\n${vertexBodyAddition}`);

    shader.fragmentShader = shader.fragmentShader
      .replace('#include <common>', `#include <common>\n${fragmentHeaderAddition}`)
      .replace('#include <map_fragment>', fragmentMapChunk)
      .replace('#include <lights_fragment_end>', `#include <lights_fragment_end>\n${sunIntensityFragmentChunk}`);
  };

  material.userData.bandUniforms = bandUniforms;
  return bandUniforms;
}

// Project the ring's shadow onto the planet body. For each surface
// fragment we trace a ray from the fragment toward the sun in the
// mesh-local frame and test whether it crosses the ring plane (Y = 0
// in object space) within the ring annulus. If so, sample the ring
// texture at the radial intersection and darken the surface beneath
// — that's how Cassini imagery shows Saturn's clouds dimmed under
// the rings when the sun is off-equator.
//
// The sun's position in mesh-local space (sunPosLocal) is updated
// from the CPU each frame because the planet's tilt + orbital
// position both factor in, and inverse(modelMatrix) per-fragment
// would be expensive.
const ringShadowFragmentChunk = `
  vec3 ringShadowSunDir = normalize(sunPosLocal - vGasLocalPos);
  if (abs(ringShadowSunDir.y) > 1e-4) {
    float ringT = -vGasLocalPos.y / ringShadowSunDir.y;
    if (ringT > 0.0) {
      vec3 ringHit = vGasLocalPos + ringShadowSunDir * ringT;
      float ringDist = length(ringHit.xz);
      // Physical penumbra: the sun's angular radius (uSunAngularRadius,
      // radians, wired per frame) smears the shadow of a ring feature by
      // ringT * angularRadius at the surface — the further the casting
      // ring is from the cloud deck, the softer its shadow edge. A
      // 3-tap blur across the ring profile at that width approximates
      // the convolution; unwired (0) collapses to the old single tap.
      float penumbraR = ringT * uSunAngularRadius;
      float ringBandWidth = max(ringOuterRadius - ringInnerRadius, 1e-4);
      float du = penumbraR / ringBandWidth;
      float uMid = (ringDist - ringInnerRadius) / ringBandWidth;
      if (uMid > -du && uMid < 1.0 + du) {
        float aMid = (uMid >= 0.0 && uMid <= 1.0)
          ? texture2D(ringShadowTexture, vec2(uMid, 0.5)).a : 0.0;
        float shadowAlpha = aMid;
        if (du > 1e-5) {
          float uLo = uMid - du;
          float uHi = uMid + du;
          float aLo = (uLo >= 0.0 && uLo <= 1.0)
            ? texture2D(ringShadowTexture, vec2(uLo, 0.5)).a : 0.0;
          float aHi = (uHi >= 0.0 && uHi <= 1.0)
            ? texture2D(ringShadowTexture, vec2(uHi, 0.5)).a : 0.0;
          shadowAlpha = aMid * 0.5 + (aLo + aHi) * 0.25;
        }
        // The ring texture's alpha encodes opacity; multiply by the
        // strength + a soft per-particle scatter floor (rings still
        // pass some light through, especially through Cassini Division).
        // Only sunlight is blocked, so the night side (lit by ringshine in
        // the famous photos) keeps its albedo.
        float ringShadowDay = smoothstep(-0.02, 0.02, dot(normalize(vGasLocalPos), ringShadowSunDir));
        float shadowAmount = shadowAlpha * ringShadowStrength * ringShadowDay;
        diffuseColor.rgb *= 1.0 - shadowAmount;
      }
    }
  }
`;

const ringShadowFragmentHeader = `
uniform sampler2D ringShadowTexture;
uniform vec3 sunPosLocal;
uniform vec3 cameraPosLocal;
uniform float ringInnerRadius;
uniform float ringOuterRadius;
uniform float ringShadowStrength;
uniform float uSunAngularRadius;
uniform float ringShineStrength;
`;

// Ringshine and the backlit limb, for the famous photos only (src/photos/:
// ringShineStrength is 0 everywhere else, so Saturn's usual look is
// untouched). The lit face of the rings fills part of the sky over the
// hemisphere on its side, and lights Saturn's night side there: Cassini's
// long exposures from Saturn's shadow show the bands by it. With the Sun
// behind the planet, the upper haze throws a thin rim of light round the
// limb.
const ringShineFragmentChunk = `
  if (ringShineStrength > 0.0) {
    vec3 rsN = normalize(vGasLocalPos);
    vec3 rsSun = normalize(sunPosLocal);
    float rsSide = sunPosLocal.y >= 0.0 ? 1.0 : -1.0;
    float rsSky = 2.0 * max(rsN.y * rsSide, 0.0) * sqrt(max(1.0 - rsN.y * rsN.y, 0.0));
    float rsNight = 1.0 - smoothstep(-0.05, 0.15, dot(rsN, rsSun));
    totalEmissiveRadiance += diffuseColor.rgb * vec3(1.0, 0.93, 0.8) * rsSky * abs(rsSun.y) * rsNight * ringShineStrength;
    // The rim: the haze's forward-scattered light lies within a fraction of
    // a per cent of a radius of the limb, under a pixel at any framing, so
    // it is drawn a pixel and a half wide (fwidth of the view cosine) the way
    // Cassini's photographs from the shadow show it, spread by the camera.
    vec3 rsView = normalize(cameraPosLocal - vGasLocalPos);
    float rsMu = clamp(dot(rsN, rsView), 0.0, 1.0);
    float rsRim = exp(-rsMu / max(0.01, 1.5 * fwidth(rsMu)));
    float rsBehind = pow(max(dot(-rsView, rsSun), 0.0), 8.0);
    totalEmissiveRadiance += vec3(1.0, 0.93, 0.82) * rsRim * rsBehind * ringShineStrength * 6.0;
  }
`;

// Atmospheric limb haze for gas giants. Real Jupiter/Saturn/Uranus/
// Neptune have visibly thick atmospheres — at the limb (where the
// view ray grazes the planet tangentially) you're looking through a
// long slab of gas, which scatters the planet's atmospheric tint
// into the line of sight and softens the silhouette against space.
// Without this term gas giants render as flat textured spheres with
// a hard edge; with it they read as 3D balls of gas.
//
// View-fresnel formulation: dot(viewDir, normal) is 1 at the centre
// of the disc and 0 at the limb, so 1 - dot is a clean limb mask.
// pow(...) sharpens the falloff so the tint hugs the rim rather than
// washing the whole disc.
const limbVertexHeader = `
varying vec3 vGasViewPos;
`;

const limbVertexBody = `
vGasViewPos = -(modelViewMatrix * vec4(position, 1.0)).xyz;
`;

const limbFragmentHeader = `
varying vec3 vGasViewPos;
uniform vec3 gasLimbColor;
uniform float gasLimbStrength;
uniform float gasLimbExponent;
uniform float gasSoftWashStrength;
uniform float gasSoftWashExponent;
`;

const limbFragmentChunk = `
{
  float gasFresnel = 1.0 - max(dot(normalize(vGasViewPos), normalize(vNormal)), 0.0);
  // Soft mid-disc wash: a very wide low-power fresnel that gently
  // tints the textured surface toward the haze color across most of
  // the disc, not just the limb. Models the photochemical /
  // ammonia-ice veil real Cassini-class imagery shows blanketing
  // Saturn (and to a lesser extent the other gas giants) — without
  // it the texture reads too sharp against space. Applied first so
  // the tight limb haze below sits on top of the washed base.
  if (gasSoftWashStrength > 0.0) {
    float softWash = pow(gasFresnel, gasSoftWashExponent);
    diffuseColor.rgb = oklchMix(diffuseColor.rgb, gasLimbColor, softWash * gasSoftWashStrength);
  }
  float limbHaze = pow(gasFresnel, gasLimbExponent);
  // OKLCh from the surface band color to the limb-haze tint so the
  // atmospheric ring around each gas giant transitions through the
  // perceptual hue arc. RGB lerp from Jupiter's orange-tan diffuse
  // to a warm-grey limb color dips through a desaturated brown
  // mid-tone; OKLCh routes through the proper amber-tinted-grey
  // sequence that real Voyager/Cassini imagery shows.
  diffuseColor.rgb = oklchMix(diffuseColor.rgb, gasLimbColor, limbHaze * gasLimbStrength);
}
`;

export function applyGasGiantLimb(material, options = {}) {
  const {
    color    = new THREE.Color(0xd6c89a),
    strength = 0.55,
    exponent = 2.4,
    // Optional soft-wash haze — a very wide fresnel pass that mildly
    // tints the entire disc toward the haze color before the tight
    // limb haze stacks on top. Used for Saturn to add the global
    // tan veil Cassini imagery shows; zero by default so Jupiter /
    // Uranus / Neptune keep their crisper texture look.
    softWashStrength = 0.0,
    softWashExponent = 0.7
  } = options;

  const limbUniforms = {
    gasLimbColor:        { value: color },
    gasLimbStrength:     { value: strength },
    gasLimbExponent:     { value: exponent },
    gasSoftWashStrength: { value: softWashStrength },
    gasSoftWashExponent: { value: softWashExponent }
  };

  const previousOnBeforeCompile = material.onBeforeCompile;
  material.onBeforeCompile = (shader) => {
    if (previousOnBeforeCompile) previousOnBeforeCompile(shader);
    injectOklabHelper(shader);
    shader.uniforms.gasLimbColor        = limbUniforms.gasLimbColor;
    shader.uniforms.gasLimbStrength     = limbUniforms.gasLimbStrength;
    shader.uniforms.gasLimbExponent     = limbUniforms.gasLimbExponent;
    shader.uniforms.gasSoftWashStrength = limbUniforms.gasSoftWashStrength;
    shader.uniforms.gasSoftWashExponent = limbUniforms.gasSoftWashExponent;

    shader.vertexShader = shader.vertexShader
      .replace('#include <common>', `#include <common>\n${limbVertexHeader}`)
      .replace('#include <begin_vertex>', `#include <begin_vertex>\n${limbVertexBody}`);
    // Append AFTER <map_fragment> so the haze tints the textured
    // diffuseColor (and any band-shifted / ring-shadowed result the
    // earlier hooks produced) instead of being immediately
    // overwritten by the texture sample.
    shader.fragmentShader = shader.fragmentShader
      .replace('#include <common>', `#include <common>\n${limbFragmentHeader}`)
      .replace('#include <map_fragment>', `#include <map_fragment>\n${limbFragmentChunk}`);
  };

  material.userData.gasLimbUniforms = limbUniforms;
  return limbUniforms;
}

// Night-side lightning for gas giants (primarily Jupiter; Cassini /
// Juno repeatedly captured genuine bolts on the dark hemisphere). The
// lightning is computed in mesh-local space because that's where the
// gas giant texture (and its differential-rotation shift) lives, so
// flash positions track the storms rather than drifting against the
// planet's rotation.
//
// Implementation: hash discrete (cell, time-bucket) pairs into rare
// flash events; each flash decays exponentially within its bucket so
// it reads as a brief pulse rather than a continuous glow. Gated by
// a smooth night-side mask AND a mid-latitude band (real Jupiter
// lightning concentrates ~30-60° latitude, never at the poles or
// dead equator). Added to totalEmissiveRadiance so the bolts show
// up where direct illumination is zero.
const lightningFragmentHeader = `
uniform vec3 lightningSunPosLocal;
uniform float lightningStrength;
`;

const lightningFragmentChunk = `
{
  vec3 lightningLocalNormal = normalize(vGasLocalPos);
  vec3 lightningSunDir = normalize(lightningSunPosLocal - vGasLocalPos);
  float lightningNight = smoothstep(0.05, 0.35, -dot(lightningLocalNormal, lightningSunDir));
  if (lightningNight > 0.04 && lightningStrength > 0.001) {
    // Mid-latitude band: bell-shaped, peaks around |lat| ≈ 0.5 (sin
    // of ~30°). Suppresses both poles and equator.
    float lightningLat = abs(lightningLocalNormal.y);
    float lightningLatGate = smoothstep(0.0, 0.35, lightningLat)
                           * smoothstep(0.85, 0.55, lightningLat);

    // Coarse cell grid in post-shift UV so flashes ride along with
    // the differential-rotation drift instead of being pinned to
    // mesh-fixed positions. Time bucket ≈ 6 buckets/sec gives an
    // average flash visibility of ~167 ms.
    vec2 lightningCell = floor(gasShiftedUv * vec2(140.0, 70.0));
    float lightningBucket = floor(bandTime * 6.0);
    float lightningHash = fract(sin(dot(lightningCell, vec2(12.9898, 78.233))
                                  + lightningBucket * 0.31415) * 43758.5453);
    // Only the rarest cells fire this bucket. 0.994 → ~0.6% of
    // mid-latitude night-side cells per bucket — sparse, like real
    // Juno imagery.
    float lightningFlash = smoothstep(0.994, 0.999, lightningHash);

    // Brief temporal pulse — exponential decay within the bucket.
    float lightningWithinBucket = fract(bandTime * 6.0);
    float lightningPulse = exp(-lightningWithinBucket * 14.0);

    // Cool blue-white. Real lightning emission peaks in violet but
    // most space photography reads it as cyan-white due to the
    // surrounding cloud reflection.
    vec3 lightningColor = vec3(0.78, 0.88, 1.0);
    totalEmissiveRadiance += lightningColor
                           * lightningFlash * lightningPulse
                           * lightningNight * lightningLatGate
                           * lightningStrength;
  }
}
`;

export function applyGasGiantLightning(material, options = {}) {
  const { strength = 1.7 } = options;

  const lightningUniforms = {
    lightningSunPosLocal: { value: new THREE.Vector3(0, 0, 0) },
    lightningStrength:    { value: strength }
  };

  const previousOnBeforeCompile = material.onBeforeCompile;
  material.onBeforeCompile = (shader) => {
    if (previousOnBeforeCompile) previousOnBeforeCompile(shader);
    shader.uniforms.lightningSunPosLocal = lightningUniforms.lightningSunPosLocal;
    shader.uniforms.lightningStrength    = lightningUniforms.lightningStrength;

    shader.fragmentShader = shader.fragmentShader
      .replace('#include <common>', `#include <common>\n${lightningFragmentHeader}`)
      .replace('#include <emissivemap_fragment>', `#include <emissivemap_fragment>\n${lightningFragmentChunk}`);
  };

  material.userData.lightningUniforms = lightningUniforms;
  return lightningUniforms;
}

export function applyRingShadowProjection(material, ringTexture, ringInner, ringOuter, options = {}) {
  const { strength = 0.85 } = options;

  const ringShadowUniforms = {
    ringShadowTexture: { value: ringTexture },
    sunPosLocal: { value: new THREE.Vector3(0, 0, 0) },
    ringInnerRadius: { value: ringInner },
    ringOuterRadius: { value: ringOuter },
    ringShadowStrength: { value: strength },
    // Sun angular radius from the planet (radians); main.js refreshes
    // it alongside sunPosLocal. 0 keeps the legacy hard-edge sample.
    uSunAngularRadius: { value: 0 },
    // The famous photos' ringshine and backlit limb (0: off).
    ringShineStrength: { value: 0 },
    cameraPosLocal: { value: new THREE.Vector3(0, 0, 0) },
  };

  const previousOnBeforeCompile = material.onBeforeCompile;
  material.onBeforeCompile = (shader) => {
    if (previousOnBeforeCompile) previousOnBeforeCompile(shader);
    Object.assign(shader.uniforms, ringShadowUniforms);

    // applyGasGiantBands usually declares vGasLocalPos in both shaders,
    // but it skips a material whose map is not loaded (a focus rebuild
    // after the map was evicted), so declare it here when it is missing.
    if (!shader.vertexShader.includes('varying vec3 vGasLocalPos;')) {
      shader.vertexShader = shader.vertexShader
        .replace('#include <common>', '#include <common>\nvarying vec3 vGasLocalPos;')
        .replace('#include <begin_vertex>', '#include <begin_vertex>\nvGasLocalPos = position;');
      shader.fragmentShader = shader.fragmentShader
        .replace('#include <common>', '#include <common>\nvarying vec3 vGasLocalPos;');
    }
    // The chunk goes after the map sample so the shadow multiplies the
    // textured diffuseColor: just before <color_fragment>, which follows
    // <map_fragment> in the standard shaders. applyGasGiantBands replaces
    // <map_fragment> whole, so anchored there (until 2 Nov 2026) the chunk
    // never reached Saturn's shader.
    shader.fragmentShader = shader.fragmentShader
      .replace('#include <common>', `#include <common>\n${ringShadowFragmentHeader}`)
      .replace('#include <color_fragment>', `${ringShadowFragmentChunk}\n#include <color_fragment>`)
      .replace('#include <emissivemap_fragment>', `#include <emissivemap_fragment>\n${ringShineFragmentChunk}`);
  };

  material.userData.ringShadowUniforms = ringShadowUniforms;
  return ringShadowUniforms;
}
